/**
 * Unified Auth Header
 * Single header component for authentication/onboarding screens
 * with centered logo and optional back button
 */

import { useState } from "react"
import { BackIcon } from "@/components/icons"
import { LogoutConfirmationModal } from "@/components/modals/logoutConfirmationModal"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { getAssetUrl } from "@/utils/assets"

interface AuthHeaderProps {
  showBackButton?: boolean
  onNavigateBack?: () => void
  extraContent?: React.ReactNode
  enableSmartNavigation?: boolean
}

export function AuthHeader({
  showBackButton = true,
  onNavigateBack,
  extraContent,
  enableSmartNavigation = false,
}: AuthHeaderProps) {
  const logout = useAuthStore(state => state.logout)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const logoLight = getAssetUrl("auth-logo.png")

  const handleBack = () => {
    try {
      logout()
      setShowLogoutModal(false)
    } catch (error) {
      console.error("Logout error:", error)
    }
  }

  const handleButtonClick = () => {
    if (enableSmartNavigation) {
      setShowLogoutModal(true)
    } else if (onNavigateBack) {
      onNavigateBack()
    }
  }

  // If extraContent is provided, use custom layout
  if (extraContent) {
    return (
      <>
        <div className="relative z-50 flex items-center justify-between px-4 h-[60px] bg-transparent">
          {/* Back Button */}
          {showBackButton && (
            <Button
              variant="ghost"
              onClick={handleButtonClick}
              className="absolute top-20 flex items-center gap-2 px-4 py-2 rounded-lg text-stone"
            >
              <BackIcon className="!w-4.5 !h-4.5" />
            </Button>
          )}

          {/* Logo - Centered */}
          <img src={logoLight} alt={"app-logo"} className="h-8 mt-2" />

          {/* Extra Content */}
          {extraContent}
        </div>
        <LogoutConfirmationModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleBack}
        />
      </>
    )
  }

  // Default layout with centered logo
  return (
    <>
      <header className="z-50 h-[60px] bg-transparent">
        <div className="relative h-full flex items-center justify-center px-4">
          {/* Back Button - Left */}
          {showBackButton && (
            <Button
              variant="outline"
              onClick={handleButtonClick}
              className="absolute left-2 md:left-14 top-4 flex items-center gap-2 px-4 py-2 border-0 text-stone"
            >
              <BackIcon className="!w-4.5 !h-4.5" />
            </Button>
          )}

          {/* Logo Branding - Centered */}
          <img src={logoLight} alt={"app-logo"} className="h-8 mt-2" />
        </div>
      </header>
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleBack}
      />
    </>
  )
}
